// src/components/admin/tabs/PromptCatalogTab.jsx
// 提示词库：集中管理在线提示词模板。左侧条目列表 + 右侧编辑器；
// 支持 9 个筛选维度、新建、在线编辑保存、恢复默认、删除/停用。
// 从 AdminDashboard.jsx 抽取：业务逻辑、API 调用与 confirm 流程逐字保留，仅重构展示与轻交互。
import { appConfirm } from '../../../services/appDialogs';
import { AppSelect } from '../../ui/AppSelect';
import React, { useCallback, useEffect, useState } from "react";
import {
  Search, RefreshCw, Plus, RotateCcw, Trash2, Save,
} from "lucide-react";
import {
  fetchPromptCatalog,
  createPromptTemplate,
  updatePromptTemplate,
  resetPromptTemplate,
  deletePromptTemplate,
} from "../../../services/adminService";
import {
  Panel,
  StatCard,
  EmptyBlock,
  EmptyText,
  ErrorNotice,
  PrimaryButton,
  SecondaryButton,
  DangerButton,
} from "../adminUi.jsx";

const ACCENT = "var(--color-warning)";

const emptyPromptDraft = () => ({
  templateKey: `admin.prompt.${Date.now()}`,
  title: '',
  modality: 'text',
  category: 'other',
  featureLocation: '通用',
  usageDescription: '',
  modelScope: 'all',
  market: 'global',
  language: 'mixed',
  sourceType: 'admin',
  sourcePath: '',
  content: '',
  defaultContent: '',
  enabled: true,
  sortOrder: 0,
  tags: '',
  metadata: '',
});

const EMPTY_FILTERS = {
  q: '',
  category: '',
  sourceType: '',
  modality: '',
  featureLocation: '',
  modelScope: '',
  language: '',
  market: '',
  enabledKey: '',
};

// 筛选下拉（带命中计数）：轻交互控件，统一样式为 h-8 + bg-tertiary + border-primary
const SelectFilter = ({ label, value, onChange, options, mapLabel }) => (
  <div>
    <label className="text-label block mb-1.5">{label}</label>
    <AppSelect
      value={value}
      onChange={event => onChange(event.target.value)}
      className="h-8 px-2 rounded-md text-xs min-w-[150px] outline-none"
      style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
    >
      <option value="">全部</option>
      {options.map(option => (
        <option key={option.value} value={option.value}>
          {(mapLabel ? mapLabel(option.label || option.value) : option.label || option.value)} ({option.count})
        </option>
      ))}
    </AppSelect>
  </div>
);

// 编辑器字段容器
const Field = ({ label, children, wide }) => (
  <label className={`flex flex-col gap-1.5 ${wide ? 'md:col-span-2' : ''}`}>
    <span className="text-label">{label}</span>
    {children}
  </label>
);

// 编辑器输入控件：bg-overlay + border-primary + rounded-md
const EDITOR_INPUT_STYLE = {
  backgroundColor: 'var(--color-bg-overlay)',
  border: '1px solid var(--color-border-primary)',
  color: 'var(--color-text-primary)',
};

const editorInputClass = 'h-8 px-2.5 rounded-md text-xs outline-none disabled:opacity-60';

// 小徽章（dirty / 分类 / 状态等标记）
const MiniBadge = ({ children, color, tint }) => (
  <span
    className="inline-flex items-center px-1.5 py-0.5 rounded text-2xs font-medium shrink-0"
    style={{
      color: color || 'var(--color-text-muted)',
      backgroundColor: tint || `color-mix(in srgb, ${color || 'var(--color-text-muted)'} 12%, transparent)`,
    }}
  >
    {children}
  </span>
);

// eslint-disable-next-line no-unused-vars -- 统一 tab props 签名，本 tab 均不使用
const PromptCatalogTab = ({ projectId, projectOptions, onProjectSelect, selectedProjectId, onProjectsChanged }) => {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [draft, setDraft] = useState(emptyPromptDraft());
  const [creating, setCreating] = useState(false);

  const items = data?.items || [];
  const selected = creating ? null : (items.find(item => String(item.id) === String(selectedId)) || items[0] || null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchPromptCatalog(filters);
      setData(res);
    } catch (e) {
      console.error(e);
      setError(e?.message || '提示词库加载失败');
    } finally {
      setLoading(false);
    }
  }, [
    filters.q,
    filters.category,
    filters.sourceType,
    filters.modality,
    filters.featureLocation,
    filters.modelScope,
    filters.language,
    filters.market,
    filters.enabledKey,
  ]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    if (creating) return;
    if (!items.length) {
      setSelectedId('');
      return;
    }
    if (!items.some(item => String(item.id) === String(selectedId))) {
      setSelectedId(items[0].id);
    }
  }, [items, selectedId, creating]);

  useEffect(() => {
    if (creating) return;
    if (!selected) {
      setDraft(emptyPromptDraft());
      return;
    }
    setDraft({
      templateKey: selected.templateKey || '',
      title: selected.title || '',
      modality: selected.modality || 'text',
      category: selected.category || 'other',
      featureLocation: selected.featureLocation || '通用',
      usageDescription: selected.usage || '',
      modelScope: selected.modelScope || 'all',
      market: selected.market || 'global',
      language: selected.language || 'mixed',
      sourceType: selected.sourceType || 'admin',
      sourcePath: selected.sourcePath || '',
      content: selected.content || '',
      defaultContent: selected.defaultContent || '',
      enabled: selected.enabled !== false,
      sortOrder: Number(selected.sortOrder || 0),
      tags: selected.tags || '',
      metadata: selected.metadata || '',
    });
  }, [selected, creating]);

  const setFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));
  const setDraftField = (key, value) => setDraft(prev => ({ ...prev, [key]: value }));
  const clearFilters = () => setFilters(EMPTY_FILTERS);

  const getOptions = (key) => data?.filters?.[key] || [];
  const modalityLabel = (value) => ({ text: '文字', image: '图片', video: '视频', other: '其他' }[value] || value);
  const languageLabel = (value) => ({ zh: '中文', en: '英文', mixed: '中英混合', unknown: '未知' }[value] || value);
  const marketLabel = (value) => ({ domestic: '国内', overseas: '出海/海外', global: '通用', unknown: '未知' }[value] || value);
  const enabledLabel = (value) => ({ enabled: '启用', disabled: '停用/回退默认' }[value] || value);

  const beginCreate = () => {
    setCreating(true);
    setSelectedId('');
    setDraft(emptyPromptDraft());
    setNotice('');
    setError('');
  };

  const saveDraft = async () => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const body = {
        ...draft,
        defaultContent: draft.defaultContent || draft.content,
      };
      if (creating) {
        const saved = await createPromptTemplate(body);
        setCreating(false);
        setSelectedId(saved?.id || '');
        setNotice('提示词已创建');
      } else if (selected?.editable) {
        await updatePromptTemplate(selected.id, body);
        setNotice('提示词已保存');
      }
      await load();
    } catch (e) {
      console.error(e);
      setError(e?.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const resetSelected = async () => {
    if (!selected?.editable) return;
    if (!(await appConfirm('确定恢复为默认提示词内容？当前在线修改会被覆盖。'))) return;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      await resetPromptTemplate(selected.id);
      setNotice('已恢复默认内容');
      await load();
    } catch (e) {
      console.error(e);
      setError(e?.message || '恢复默认失败');
    } finally {
      setSaving(false);
    }
  };

  const deleteSelected = async () => {
    if (!selected?.editable) return;
    const hardDelete = selected.sourceType === 'admin';
    const message = hardDelete
      ? '确定删除这个后台创建的提示词？'
      : '资源/代码同步来的提示词不会物理删除，将停用并回退默认内容。确定继续？';
    if (!(await appConfirm(message))) return;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      await deletePromptTemplate(selected.id);
      setSelectedId('');
      setNotice(hardDelete ? '提示词已删除' : '提示词已停用并回退默认');
      await load();
    } catch (e) {
      console.error(e);
      setError(e?.message || '删除失败');
    } finally {
      setSaving(false);
    }
  };

  const currentEditable = creating || selected?.editable;

  return (
    <div className="space-y-4">
      {/* 筛选 + 概览 */}
      <Panel
        title="提示词库"
        sub="集中管理在线提示词模板"
        right={(
          <PrimaryButton onClick={beginCreate} className="inline-flex items-center gap-1.5">
            <Plus size={13} />
            新建提示词
          </PrimaryButton>
        )}
      >
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[260px]">
            <label className="text-label block mb-1.5">搜索</label>
            <div className="flex items-center gap-2 h-8 px-2.5 rounded-md" style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)' }}>
              <Search size={13} style={{ color: 'var(--color-text-muted)' }} />
              <input
                value={filters.q}
                onChange={event => setFilter('q', event.target.value)}
                placeholder="搜标题、来源路径、提示词内容"
                className="flex-1 bg-transparent outline-none text-xs"
                style={{ color: 'var(--color-text-primary)' }}
              />
            </div>
          </div>
          <SelectFilter label="分类" value={filters.category} onChange={v => setFilter('category', v)} options={getOptions('categories')} />
          <SelectFilter label="类型" value={filters.modality} onChange={v => setFilter('modality', v)} options={getOptions('modalities')} mapLabel={modalityLabel} />
          <SelectFilter label="功能位置" value={filters.featureLocation} onChange={v => setFilter('featureLocation', v)} options={getOptions('featureLocations')} />
          <SelectFilter label="模型" value={filters.modelScope} onChange={v => setFilter('modelScope', v)} options={getOptions('modelScopes')} />
          <SelectFilter label="来源" value={filters.sourceType} onChange={v => setFilter('sourceType', v)} options={getOptions('sourceTypes')} />
          <SelectFilter label="语言" value={filters.language} onChange={v => setFilter('language', v)} options={getOptions('languages')} mapLabel={languageLabel} />
          <SelectFilter label="项目域" value={filters.market} onChange={v => setFilter('market', v)} options={getOptions('markets')} mapLabel={marketLabel} />
          <SelectFilter label="状态" value={filters.enabledKey} onChange={v => setFilter('enabledKey', v)} options={getOptions('enabled')} mapLabel={enabledLabel} />
          <SecondaryButton onClick={load} disabled={loading} className="inline-flex items-center gap-1.5">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
            刷新
          </SecondaryButton>
          <SecondaryButton onClick={clearFilters}>
            清空
          </SecondaryButton>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          <StatCard label="总提示词条目" value={data?.total ?? '-'} />
          <StatCard label="当前筛选命中" value={data?.filteredTotal ?? '-'} />
          <StatCard label="文字 / 图片 / 视频" value={`${(data?.filters?.modalities || []).find(x => x.value === 'text')?.count ?? 0}/${(data?.filters?.modalities || []).find(x => x.value === 'image')?.count ?? 0}/${(data?.filters?.modalities || []).find(x => x.value === 'video')?.count ?? 0}`} />
          <StatCard label="已停用" value={(data?.filters?.enabled || []).find(x => x.value === 'disabled')?.count ?? 0} />
        </div>
        <div className="text-xs mt-3" style={{ color: 'var(--color-text-muted)' }}>
          {data?.note || '提示词模板会按默认值同步到数据库，业务接入 templateKey 后优先读取在线内容。'}
        </div>
      </Panel>

      {error && <ErrorNotice message={error} />}
      {notice && (
        <div className="rounded-lg px-3.5 py-2.5 text-sm animate-in fade-in slide-in-from-top-1 duration-200" style={{ backgroundColor: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)', color: 'var(--color-success)' }}>
          {notice}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[390px_minmax(0,1fr)] gap-4 items-start">
        {/* 左：条目列表 */}
        <Panel
          title="提示词条目"
          right={<span className="font-mono-num text-2xs" style={{ color: 'var(--color-text-muted)' }}>{loading ? '加载中' : `${items.length} 条`}</span>}
        >
          {loading && !items.length ? (
            <div className="space-y-2">
              {[0, 1, 2, 3, 4].map(i => <div key={i} className="skeleton h-20" />)}
            </div>
          ) : !items.length ? (
            <EmptyText text="暂无匹配提示词" />
          ) : (
            <div className="space-y-1 max-h-[680px] overflow-y-auto custom-scrollbar -mr-1 pr-1">
              {items.map(item => {
                const active = !creating && String(selected?.id) === String(item.id);
                return (
                  <button
                    type="button"
                    key={item.id}
                    onClick={() => setSelectedId(item.id)}
                    className="w-full text-left rounded-md px-2.5 py-2 transition-colors duration-150 cursor-pointer"
                    style={{
                      backgroundColor: active ? 'color-mix(in srgb, var(--color-warning) 10%, transparent)' : 'transparent',
                      boxShadow: active ? 'inset 2px 0 0 var(--color-warning)' : 'none',
                      color: 'var(--color-text-primary)',
                    }}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="text-[13px] font-medium truncate">{item.title}</div>
                      <MiniBadge>{item.sourceTypeLabel}</MiniBadge>
                    </div>
                    <div className="font-mono-num text-2xs mb-1 truncate" style={{ color: 'var(--color-text-muted)' }}>{item.sourcePath}{item.lineNumber ? `:${item.lineNumber}` : ''}</div>
                    <div className="text-2xs mb-1.5 truncate" style={{ color: 'var(--color-text-muted)' }}>用途：{item.usage || '未标注'}</div>
                    <div className="flex flex-wrap gap-1 mb-1.5">
                      <MiniBadge color={ACCENT}>{item.categoryLabel}</MiniBadge>
                      <MiniBadge color="var(--color-text-secondary)">{item.modalityLabel || modalityLabel(item.modality)}</MiniBadge>
                      <MiniBadge color={item.enabled === false ? 'var(--color-error)' : 'var(--color-success)'}>{item.enabled === false ? '停用' : '启用'}</MiniBadge>
                      <MiniBadge color="var(--color-text-secondary)">{languageLabel(item.language)}</MiniBadge>
                      <MiniBadge color="var(--color-text-secondary)">{marketLabel(item.market)}</MiniBadge>
                    </div>
                    <div className="text-xs line-clamp-2" style={{ color: 'var(--color-text-secondary)' }}>{item.excerpt || '无预览'}</div>
                  </button>
                );
              })}
            </div>
          )}
        </Panel>

        {/* 右：编辑器 */}
        <Panel
          title={creating ? '新建提示词' : (selected ? selected.title : '请选择提示词')}
          sub={creating ? '后台创建的提示词模板' : (selected ? `${selected.sourcePath || ''}${selected.lineNumber ? `:${selected.lineNumber}` : ''}` : undefined)}
          right={(creating || selected) ? (
            <div className="flex flex-wrap gap-1.5">
              {selected?.changed && <MiniBadge color={ACCENT}>已改写</MiniBadge>}
              <MiniBadge color={ACCENT}>{draft.category || 'other'}</MiniBadge>
              <MiniBadge color="var(--color-text-secondary)">{modalityLabel(draft.modality)}</MiniBadge>
              <MiniBadge color={draft.enabled ? 'var(--color-success)' : 'var(--color-error)'}>{draft.enabled ? '启用' : '停用'}</MiniBadge>
            </div>
          ) : undefined}
        >
          {creating || selected ? (
            <div className="space-y-4">
              <div className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>
                templateKey：<span className="font-mono-num">{draft.templateKey || '-'}</span> · 模型：<span className="font-mono-num">{draft.modelScope || 'all'}</span> · 项目域：{marketLabel(draft.market)} · 语言：{languageLabel(draft.language)}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Template Key">
                  <input value={draft.templateKey} disabled={!creating} onChange={e => setDraftField('templateKey', e.target.value)} className={editorInputClass} style={EDITOR_INPUT_STYLE} />
                </Field>
                <Field label="标题">
                  <input value={draft.title} disabled={!currentEditable} onChange={e => setDraftField('title', e.target.value)} className={editorInputClass} style={EDITOR_INPUT_STYLE} />
                </Field>
                <Field label="类型">
                  <AppSelect value={draft.modality} disabled={!currentEditable} onChange={e => setDraftField('modality', e.target.value)} className={editorInputClass} style={EDITOR_INPUT_STYLE}>
                    <option value="text">文字</option>
                    <option value="image">图片</option>
                    <option value="video">视频</option>
                    <option value="other">其他</option>
                  </AppSelect>
                </Field>
                <Field label="分类">
                  <input value={draft.category} disabled={!currentEditable} onChange={e => setDraftField('category', e.target.value)} className={editorInputClass} style={EDITOR_INPUT_STYLE} />
                </Field>
                <Field label="功能位置">
                  <input value={draft.featureLocation} disabled={!currentEditable} onChange={e => setDraftField('featureLocation', e.target.value)} className={editorInputClass} style={EDITOR_INPUT_STYLE} placeholder="例如：小故事板 / 角色工坊 / 音乐音频" />
                </Field>
                <Field label="模型范围">
                  <input value={draft.modelScope} disabled={!currentEditable} onChange={e => setDraftField('modelScope', e.target.value)} className={editorInputClass} style={EDITOR_INPUT_STYLE} placeholder="all / guavideo / gemini / chatgpt" />
                </Field>
                <Field label="项目域">
                  <AppSelect value={draft.market} disabled={!currentEditable} onChange={e => setDraftField('market', e.target.value)} className={editorInputClass} style={EDITOR_INPUT_STYLE}>
                    <option value="global">通用</option>
                    <option value="domestic">国内</option>
                    <option value="overseas">出海/海外</option>
                  </AppSelect>
                </Field>
                <Field label="语言">
                  <AppSelect value={draft.language} disabled={!currentEditable} onChange={e => setDraftField('language', e.target.value)} className={editorInputClass} style={EDITOR_INPUT_STYLE}>
                    <option value="zh">中文</option>
                    <option value="en">英文</option>
                    <option value="mixed">中英混合</option>
                    <option value="unknown">未知</option>
                  </AppSelect>
                </Field>
                <Field label="来源">
                  <input value={draft.sourceType} disabled={!currentEditable} onChange={e => setDraftField('sourceType', e.target.value)} className={editorInputClass} style={EDITOR_INPUT_STYLE} />
                </Field>
                <Field label="排序">
                  <input type="number" value={draft.sortOrder} disabled={!currentEditable} onChange={e => setDraftField('sortOrder', Number(e.target.value || 0))} className={`${editorInputClass} font-mono-num`} style={EDITOR_INPUT_STYLE} />
                </Field>
                <Field label="来源路径" wide>
                  <input value={draft.sourcePath} disabled={!currentEditable} onChange={e => setDraftField('sourcePath', e.target.value)} className={`${editorInputClass} font-mono-num`} style={EDITOR_INPUT_STYLE} />
                </Field>
                <Field label="用途说明" wide>
                  <textarea value={draft.usageDescription} disabled={!currentEditable} onChange={e => setDraftField('usageDescription', e.target.value)} rows={2} className="px-2.5 py-2 rounded-md text-xs outline-none disabled:opacity-60" style={EDITOR_INPUT_STYLE} />
                </Field>
                <Field label="标签" wide>
                  <input value={draft.tags} disabled={!currentEditable} onChange={e => setDraftField('tags', e.target.value)} className={editorInputClass} style={EDITOR_INPUT_STYLE} placeholder="逗号分隔" />
                </Field>
              </div>

              <label className="flex items-center gap-2 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                <input type="checkbox" checked={!!draft.enabled} disabled={!currentEditable} onChange={e => setDraftField('enabled', e.target.checked)} />
                启用在线内容；关闭时运行时回退默认提示词
              </label>

              <div>
                <div className="text-label mb-1.5">在线提示词内容</div>
                <textarea
                  value={draft.content}
                  disabled={!currentEditable}
                  onChange={e => setDraftField('content', e.target.value)}
                  rows={18}
                  className="w-full px-3 py-2.5 rounded-md text-xs leading-6 font-mono-num outline-none disabled:opacity-60 custom-scrollbar"
                  style={{ ...EDITOR_INPUT_STYLE, minHeight: 420 }}
                />
              </div>

              <details className="rounded-md" style={{ backgroundColor: 'var(--color-bg-overlay)', border: '1px solid var(--color-border-secondary)' }}>
                <summary className="px-3 py-2.5 cursor-pointer text-xs font-semibold" style={{ color: 'var(--color-text-secondary)' }}>默认提示词对照</summary>
                <textarea
                  value={draft.defaultContent}
                  disabled={!currentEditable}
                  onChange={e => setDraftField('defaultContent', e.target.value)}
                  rows={10}
                  className="w-full px-3 py-2.5 bg-transparent text-xs leading-6 font-mono-num outline-none custom-scrollbar"
                  style={{ color: 'var(--color-text-muted)' }}
                />
              </details>

              <div className="flex flex-wrap gap-2 justify-end">
                {creating && (
                  <SecondaryButton onClick={() => setCreating(false)}>
                    取消新建
                  </SecondaryButton>
                )}
                {!creating && selected?.editable && (
                  <>
                    <SecondaryButton onClick={resetSelected} disabled={saving} className="inline-flex items-center gap-1.5">
                      <RotateCcw size={12} />
                      恢复默认
                    </SecondaryButton>
                    <DangerButton onClick={deleteSelected} disabled={saving} className="inline-flex items-center gap-1.5">
                      <Trash2 size={12} />
                      {selected.sourceType === 'admin' ? '删除' : '停用'}
                    </DangerButton>
                  </>
                )}
                <PrimaryButton onClick={saveDraft} disabled={saving || !currentEditable} className="inline-flex items-center gap-1.5">
                  <Save size={12} />
                  {saving ? '保存中…' : '保存'}
                </PrimaryButton>
              </div>
            </div>
          ) : (
            <EmptyBlock text="左侧选择一个条目查看内容" />
          )}
        </Panel>
      </div>
    </div>
  );
};

export default PromptCatalogTab;
